import React from 'react';
import { Box, Typography, useTheme, useMediaQuery, Paper } from '@mui/material';
import { motion } from 'framer-motion';

export const FeatureItem = ({
  title,
  description,
  icon,
  color,
  delay = 0
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Animation variants
  const iconAnimation = {
    initial: { scale: 0.8, opacity: 0 },
    whileInView: { scale: 1, opacity: 1 },
    whileHover: { rotate: [0, -10, 10, -5, 0], transition: { duration: 0.5 } }
  };
  
  const underlineAnimation = {
    initial: { width: 0 },
    whileInView: { width: '60px' },
    transition: { delay: delay + 0.3, duration: 0.5 }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.7, delay, ease: "easeOut" }}
      style={{ 
        height: '100%',
        marginBottom: isMobile ? '40px' : '28px'
      }}
    >
      <Paper
        elevation={2}
        sx={{
          p: 4,
          pt: 5,
          pb: 4,
          borderRadius: 4,
          height: '100%',
          bgcolor: 'background.paper',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
          transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center', // Center all content
          textAlign: 'center',
          '&:hover': {
            transform: 'translateY(-12px)',
            boxShadow: `0 20px 40px rgba(0, 0, 0, 0.15), 0 0 20px ${color}30`
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '6px',
            background: `linear-gradient(90deg, ${color}90, ${color})`,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: '10%',
            width: '80%', 
            height: '1px',
            background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
          }
        }}
      >
        {/* Decorative background circles */}
        <Box
          sx={{
            position: 'absolute',
            top: 40,
            right: -20,
            width: 120,
            height: 120,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${color}10 0%, transparent 70%)`,
            zIndex: 0
          }}
        />
        
        <Box
          sx={{
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 160,
            height: 160,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${color}08 0%, transparent 60%)`,
            zIndex: 0
          }}
        />
        
        {/* Icon container */}
        <motion.div
          variants={iconAnimation}
          initial="initial"
          whileInView="whileInView"
          whileHover="whileHover"
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: delay + 0.2 }}
          style={{ marginBottom: 24, position: 'relative', zIndex: 2 }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '24px',
              background: `linear-gradient(135deg, ${color}90 0%, ${color} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              boxShadow: `0 10px 20px ${color}40`,
              position: 'relative',
              mb: 2.5
            }}
          >
            <Box
              component="img"
              src={icon}
              alt={title}
              sx={{ 
                width: 36, 
                height: 36,
                filter: 'brightness(0) invert(1)',
                transition: 'all 0.3s ease'
              }}
            />
            
            {/* Decorative dots */}
            <Box
              sx={{
                position: 'absolute',
                top: -6,
                right: -6,
                width: 16, 
                height: 16,
                borderRadius: '50%',
                bgcolor: theme.palette.background.paper,
                border: `3px solid ${color}`
              }}
            />
          </Box>
        </motion.div>
        
        {/* Title */}
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 700,
            fontSize: '1.4rem',
            lineHeight: 1.3,
            mb: 2,
            position: 'relative',
            zIndex: 2,
            px: 1
          }}
        >
          {title}
          <motion.div
            initial={underlineAnimation.initial}
            whileInView={underlineAnimation.whileInView}
            transition={underlineAnimation.transition}
            viewport={{ once: true }}
            style={{
              height: '3px',
              background: color,
              borderRadius: '2px',
              margin: '8px auto 0',
              opacity: 0.7
            }}
          />
        </Typography>
        
        {/* Description */}
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{
            lineHeight: 1.8,
            flexGrow: 1,
            mb: 3,
            position: 'relative',
            zIndex: 2,
            fontSize: '1rem',
            px: 1
          }}
        >
          {description}
        </Typography>
        
        {/* Animated learn more indicator */}
        {/* <motion.div
          whileHover={{
            x: [0, 5, 0],
            transition: { repeat: Infinity, duration: 1 }
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${color}40 0%, ${color}70 100%)`,
              color: theme.palette.mode === 'dark' ? 'white' : color,
              fontSize: 20,
              mt: 'auto',
              cursor: 'pointer',
              opacity: 0.9
            }}
          >
            →
          </Box>
        </motion.div> */}
      </Paper>
    </motion.div>
  );
};
